import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Demo from '../../components/Demo';
import { SettingsPopup } from '../settingsPopup';
import data from '../../data';
import styled from 'styled-components';
import Svg from 'react-inlinesvg';
import ClickPoint from '../../assets/ClickPoint.svg';
import Logo from '../../assets/SailPoint-Logo.svg';
import { ArrowBack } from '../../components/Demo/styled';
import {
  ImageContainer,
  RelativeWrapper,
  imageContainerStyle,
  BackgroundImage,
  ClickContainerCity,
  Button,
  NameContainer,
  LogoContainer,
  BackToTheCityContainer,
  SettingsContainer,
  Listings
} from '../styled';
import SettingsIcon from '../../assets/Settings.svg';
import ListView from '../../assets/ListView.svg';
import DemosPopup from '../demosPopup';
import { useNavigationState } from '../../contexts/navigationState';

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: black;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  pointer-events: none;
  transition: opacity 500ms ease-in-out;
  z-index: 7;
`;

const BlurOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 8;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);
`;

const VideoLayer = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 6;
`;

const SpinnerContainer = styled.div`
  position: absolute;
  bottom: 24px;
  right: 24px;
  z-index: 8;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  background: rgba(0, 0, 0, 0.4);
  padding: 8px 12px;
  border-radius: 8px;
  font-family: Poppins, sans-serif;
  font-size: 14px;
`;

const SpinnerDot = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// (cards removed; using clickpoints on the map instead)

// const BackButton = styled.div`
//   position: absolute;
//   top: 24px;
//   left: 24px;
//   z-index: 3;
//   background: rgba(0, 0, 0, 0.6);
//   color: white;
//   padding: 10px 14px;
//   border-radius: 6px;
//   cursor: pointer;
//   user-select: none;
// `;

export default function Manufacturing() {
  const [scene, setScene] = useState('overview'); // overview | assembly | office | logistics
  const [fading, setFading] = useState(false);
  const navigate = useNavigate();
  const [showAssemblyFlyover, setShowAssemblyFlyover] = useState(false);
  const [showOfficeFlyover, setShowOfficeFlyover] = useState(false);
  const [showLogisticsFlyover, setShowLogisticsFlyover] = useState(false);
  const videoPreloadRef = React.useRef(null);
  const [flyoverReadyAssembly, setFlyoverReadyAssembly] = useState(false);
  const [preloadingAssembly, setPreloadingAssembly] = useState(false);
  const [flyoverReadyOffice, setFlyoverReadyOffice] = useState(false);
  const [preloadingOffice, setPreloadingOffice] = useState(false);
  const [flyoverReadyLogistics, setFlyoverReadyLogistics] = useState(false);
  const [preloadingLogistics, setPreloadingLogistics] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [closing, setClosing] = useState(true);
  const [toggleValue, setToggleValue] = useState(() => localStorage.getItem('toggle') === 'true');
  const [sliderValue, setSliderValue] = useState(() => parseInt(localStorage.getItem('slider')) || 5);
  const [animationsDisabled, setAnimationsDisabled] = useState(
    () => localStorage.getItem('animationsDisabled') === 'true'
  );
  const [demosVisible, setDemosVisible] = useState(false);
  const { setDemoId } = useNavigationState();

  // Build overview demos grouped by subscene; computed unconditionally to preserve hook order
  const overviewDemos = useMemo(() => {
    const list = data.manufacturing.list || [];
    const pins = (data.manufacturing.scenePins && data.manufacturing.scenePins.logistics) || {};
    const expanded = [];
    list.forEach((demo) => {
      const subs = Array.isArray(demo.subscenes) ? demo.subscenes : [];
      if (subs.includes('assembly')) expanded.push({ ...demo, categories: ['Assembly'] });
      if (subs.includes('office')) expanded.push({ ...demo, categories: ['Office'] });
      if (subs.includes('logistics') || pins[demo.name]) expanded.push({ ...demo, categories: ['Logistics'] });
    });
    expanded.sort((a, b) => {
      if (a.categories[0] < b.categories[0]) return -1;
      if (a.categories[0] > b.categories[0]) return 1;
      return a.name.localeCompare(b.name);
    });
    return expanded;
  }, [data.manufacturing]);

  // Preload all images so transitions are smooth
  useEffect(() => {
    const imageUrls = [
      '/full-factory.png',
      '/assembly.png',
      '/office.png',
      '/logistics.png',
      `/${data.manufacturing.imageUrl || 'manufacturing.jpg'}`
    ];
    imageUrls.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Preload the assembly flyover video on overview
  useEffect(() => {
    const animationsDisabled = localStorage.getItem('animationsDisabled') === 'true';
    if (animationsDisabled) return;
    if (scene !== 'overview') return;
    if (flyoverReadyAssembly || preloadingAssembly) return;
    setPreloadingAssembly(true);
    const v = document.createElement('video');
    v.src = '/assembly-flyover.mp4';
    v.preload = 'auto';
    v.muted = true;
    v.playsInline = true;
    const onCanPlay = () => {
      setFlyoverReadyAssembly(true);
      setPreloadingAssembly(false);
      v.removeEventListener('canplaythrough', onCanPlay);
      v.remove();
    };
    const onError = () => {
      setPreloadingAssembly(false);
      v.removeEventListener('error', onError);
      v.remove();
    };
    v.addEventListener('canplaythrough', onCanPlay);
    v.addEventListener('error', onError);
    v.load();
    videoPreloadRef.current = v;
  }, [scene, flyoverReadyAssembly, preloadingAssembly]);

  // Preload the office flyover video on overview
  useEffect(() => {
    const animationsDisabled = localStorage.getItem('animationsDisabled') === 'true';
    if (animationsDisabled) return;
    if (scene !== 'overview') return;
    if (flyoverReadyOffice || preloadingOffice) return;
    setPreloadingOffice(true);
    const v = document.createElement('video');
    v.src = '/office-flyover.mp4';
    v.preload = 'auto';
    v.muted = true;
    v.playsInline = true;
    const onCanPlay = () => {
      setFlyoverReadyOffice(true);
      setPreloadingOffice(false);
      v.removeEventListener('canplaythrough', onCanPlay);
      v.remove();
    };
    const onError = () => {
      setPreloadingOffice(false);
      v.removeEventListener('error', onError);
      v.remove();
    };
    v.addEventListener('canplaythrough', onCanPlay);
    v.addEventListener('error', onError);
    v.load();
  }, [scene, flyoverReadyOffice, preloadingOffice]);

  // Preload the logistics flyover video on overview
  useEffect(() => {
    const animationsDisabled = localStorage.getItem('animationsDisabled') === 'true';
    if (animationsDisabled) return;
    if (scene !== 'overview') return;
    if (flyoverReadyLogistics || preloadingLogistics) return;
    setPreloadingLogistics(true);
    const v = document.createElement('video');
    v.src = '/logistics-flyover.mp4';
    v.preload = 'auto';
    v.muted = true;
    v.playsInline = true;
    const onCanPlay = () => {
      setFlyoverReadyLogistics(true);
      setPreloadingLogistics(false);
      v.removeEventListener('canplaythrough', onCanPlay);
      v.remove();
    };
    const onError = () => {
      setPreloadingLogistics(false);
      v.removeEventListener('error', onError);
      v.remove();
    };
    v.addEventListener('canplaythrough', onCanPlay);
    v.addEventListener('error', onError);
    v.load();
  }, [scene, flyoverReadyLogistics, preloadingLogistics]);

  const sceneData = useMemo(() => {
    if (scene === 'assembly') {
      const all = data.manufacturing.list || [];
      const hasAssemblyTags = all.some((d) => Array.isArray(d.subscenes) && d.subscenes.includes('assembly'));
      const base = hasAssemblyTags
        ? all.filter((d) => d.subscenes && d.subscenes.includes('assembly'))
        : all.filter((d) => d.name !== 'Manage third-party non-employee identities');
      const list = base.map((d) => ({ ...d, categories: ['Manufacturing'] }));
      return { ...data.manufacturing, imageUrl: 'assembly.png', list };
    }
    if (scene === 'office') {
      const all = data.manufacturing.list || [];
      const hasOfficeTags = all.some((d) => Array.isArray(d.subscenes) && d.subscenes.includes('office'));
      const baseOffice = hasOfficeTags ? all.filter((d) => d.subscenes && d.subscenes.includes('office')) : [];
      const list = baseOffice.map((d) => ({ ...d, categories: ['Manufacturing'] }));
      return { ...data.manufacturing, imageUrl: 'office.png', list };
    }
    if (scene === 'logistics') {
      const logisticsPins = (data.manufacturing.scenePins && data.manufacturing.scenePins.logistics) || {};
      const names = Object.keys(logisticsPins);

      const resolveByName = (name) => {
        return (data.manufacturing.list || []).find((d) => d.name === name) || null;
      };

      let list = names
        .map((name) => {
          const demo = resolveByName(name);
          if (!demo) return null;
          return {
            ...demo,
            categories: ['Manufacturing'],
            pinLocation: (data.manufacturing.scenePins &&
              data.manufacturing.scenePins.logistics &&
              data.manufacturing.scenePins.logistics[demo.name]) ||
              (demo.scenePins && demo.scenePins.logistics) ||
              demo.pinLocation || { x: 50, y: 50 }
          };
        })
        .filter(Boolean);

      // Fallback to legacy three if no names configured
      if (list.length === 0) {
        const nerm = (data.manufacturing.list || []).find(
          (d) => d.name === 'Manage third-party non-employee identities'
        );
        const reviewUncorr = (data.office.list || []).find((d) => d.name === 'Review access for uncorrelated accounts');
        const dynamicRoles = (data.finance.list || []).find((d) => d.name === 'Manage dynamic access roles');
        const toItem = (demo) =>
          demo
            ? {
                ...demo,
                categories: ['Manufacturing'],
                pinLocation: (data.manufacturing.scenePins &&
                  data.manufacturing.scenePins.logistics &&
                  data.manufacturing.scenePins.logistics[demo.name]) ||
                  (demo.scenePins && demo.scenePins.logistics) ||
                  demo.pinLocation || { x: 50, y: 50 }
              }
            : null;
        list = [toItem(nerm), toItem(reviewUncorr), toItem(dynamicRoles)].filter(Boolean);
      }

      // Keep original ids from manufacturing list
      // Keep original ids from manufacturing list

      return { imageUrl: 'logistics.png', list };
    }
    return null;
  }, [scene]);

  const goTo = (next) => {
    setFading(true);
    setTimeout(() => {
      setScene(next);
      setFading(false);
    }, 500);
  };

  const startAssembly = () => {
    const animationsDisabled = localStorage.getItem('animationsDisabled') === 'true';
    // Fade to black first
    setFading(true);
    setTimeout(() => {
      if (animationsDisabled) {
        setScene('assembly');
        setFading(false);
      } else {
        setShowAssemblyFlyover(true);
        setFading(false);
      }
    }, 500);
  };

  const onAssemblyFlyoverEnd = () => {
    // Fade to black for 500ms, then switch scene and hide video
    setFading(true);
    setTimeout(() => {
      setScene('assembly');
      setShowAssemblyFlyover(false);
      setFading(false);
    }, 500);
  };

  const startOffice = () => {
    const animationsDisabled = localStorage.getItem('animationsDisabled') === 'true';
    setFading(true);
    setTimeout(() => {
      if (animationsDisabled) {
        setScene('office');
        setFading(false);
      } else {
        setShowOfficeFlyover(true);
        setFading(false);
      }
    }, 500);
  };

  const startLogistics = () => {
    const animationsDisabled = localStorage.getItem('animationsDisabled') === 'true';
    setFading(true);
    setTimeout(() => {
      if (animationsDisabled) {
        setScene('logistics');
        setFading(false);
      } else {
        setShowLogisticsFlyover(true);
        setFading(false);
      }
    }, 500);
  };

  const onOfficeFlyoverEnd = () => {
    setFading(true);
    setTimeout(() => {
      setScene('office');
      setShowOfficeFlyover(false);
      setFading(false);
    }, 500);
  };

  const goBackToFactory = () => {
    goTo('overview');
  };

  const onSettingsClose = () => {
    setSettingsVisible(false);
  };

  const onConfigurationSave = (value) => {
    localStorage.setItem('toggle', value.toggleValue);
    setToggleValue(value.toggleValue);
    localStorage.setItem('slider', value.sliderValue);
    setSliderValue(value.sliderValue);
    if (typeof value.animationsDisabled === 'boolean') {
      localStorage.setItem('animationsDisabled', value.animationsDisabled);
      setAnimationsDisabled(value.animationsDisabled);
    }
  };

  const onOpenListView = () => {
    setClosing(true);
    setDemosVisible(true);
  };

  const onListViewClose = () => {
    setDemosVisible(false);
  };

  if (scene !== 'overview' && sceneData) {
    return (
      <>
        <Demo data={sceneData} onBack={goBackToFactory} backLabel="Back to factory" showAnimationsToggle hideListIcon />
        <Overlay visible={fading} />
      </>
    );
  }

  return (
    <>
      <ImageContainer style={imageContainerStyle}>
        <RelativeWrapper>
          <BackgroundImage src={`../../full-factory.png`} />

          <LogoContainer>
            <Svg src={Logo} style={{ marginLeft: '-20px', marginTop: '-20px', width: '287px' }} />
          </LogoContainer>
          <BackToTheCityContainer onClick={() => navigate('/', { state: { onBack: true } })}>
            <ArrowBack src={'/Arrow.svg'} />
            Back to the city
          </BackToTheCityContainer>

          <SettingsContainer
            blurred={false}
            blurRatio={0}
            onClick={() => {
              setClosing(true);
              setSettingsVisible(true);
            }}
            pointerVisibility={true}
          >
            <Svg src={SettingsIcon} style={{ width: '30px' }} />
          </SettingsContainer>

          <Listings blurred={false} blurRatio={0} onClick={onOpenListView} pointerVisibility={!settingsVisible}>
            <Svg src={ListView} style={{ width: '30px' }} />
          </Listings>

          {(
            (data.manufacturing && data.manufacturing.entryPoints) || [
              { id: 1, name: 'Assembly Line', key: 'assembly', pinLocation: { x: 44, y: 26 } },
              { id: 2, name: 'Office', key: 'office', pinLocation: { x: 80, y: 49 } },
              { id: 3, name: 'Logistics', key: 'logistics', pinLocation: { x: 13, y: 49 } }
            ]
          ).map((p) => (
            <div key={p.id}>
              <ClickContainerCity top={p.pinLocation.y} left={p.pinLocation.x} position={'BOTTOM'}>
                <Button
                  onClick={() =>
                    p.key === 'assembly' ? startAssembly() : p.key === 'office' ? startOffice() : startLogistics()
                  }
                  pointerVisibility={!settingsVisible}
                >
                  <Svg src={ClickPoint} />
                </Button>
              </ClickContainerCity>
              <Button
                onClick={() =>
                  p.key === 'assembly' ? startAssembly() : p.key === 'office' ? startOffice() : startLogistics()
                }
                pointerVisibility={!settingsVisible}
              >
                <NameContainer top={p.pinLocation.y} left={p.pinLocation.x}>
                  {p.name}
                </NameContainer>
              </Button>
            </div>
          ))}

          {showAssemblyFlyover && (
            <VideoLayer
              src="/assembly-flyover.mp4"
              autoPlay
              muted
              onEnded={onAssemblyFlyoverEnd}
              onClick={onAssemblyFlyoverEnd}
            />
          )}
          {showOfficeFlyover && (
            <VideoLayer
              src="/office-flyover.mp4"
              autoPlay
              muted
              onEnded={onOfficeFlyoverEnd}
              onClick={onOfficeFlyoverEnd}
            />
          )}
          {showLogisticsFlyover && (
            <VideoLayer
              src="/logistics-flyover.mp4"
              autoPlay
              muted
              onEnded={() => {
                setFading(true);
                setTimeout(() => {
                  setScene('logistics');
                  setShowLogisticsFlyover(false);
                  setFading(false);
                }, 500);
              }}
              onClick={() => {
                setFading(true);
                setTimeout(() => {
                  setScene('logistics');
                  setShowLogisticsFlyover(false);
                  setFading(false);
                }, 500);
              }}
            />
          )}
          {scene === 'overview' &&
            localStorage.getItem('animationsDisabled') !== 'true' &&
            (preloadingAssembly || preloadingOffice || preloadingLogistics) &&
            !(flyoverReadyAssembly && flyoverReadyOffice && flyoverReadyLogistics) && (
              <SpinnerContainer>
                <SpinnerDot />
                Preloading transition…
              </SpinnerContainer>
            )}
          <Overlay visible={fading} />
          {settingsVisible && <BlurOverlay />}
        </RelativeWrapper>
      </ImageContainer>
      {settingsVisible && (
        <SettingsPopup
          visible={closing}
          toggle={toggleValue}
          slider={sliderValue}
          animationsDisabled={animationsDisabled}
          showAnimationsToggle
          onClose={onSettingsClose}
          onSave={onConfigurationSave}
        />
      )}
      {demosVisible && (
        <DemosPopup
          visible={closing}
          onClose={onListViewClose}
          industryList={overviewDemos}
          onStartClick={(id, demo) => {
            const cat = (demo.categories && demo.categories[0]) || '';
            if (cat === 'Assembly') setScene('assembly');
            else if (cat === 'Office') setScene('office');
            else if (cat === 'Logistics') setScene('logistics');
            setDemoId(id);
            setDemosVisible(false);
          }}
        />
      )}
    </>
  );
}
